import React, { useState, useEffect, useMemo } from "react";
import Model, { helper } from "@tripian/model";
import { Button, CustomSlider, PreLoading, SvgIcons } from "@tripian/react";
import { useParams } from "react-router";
import useFavorite from "../../hooks/useFavorite";
import PoiCard from "../PoiCard/PoiCard";

interface ISearchPlace {
  // for input
  query: string[] | undefined;
  poiCategoryIds: number[];
  // recommendedPois: Model.Poi[];
  search: (
    query: string[] | undefined,
    searchPoiCategories?: number[],
    showOffersOnly?: boolean,
    showBookingsOnly?: boolean,
    showBookingsRestaurant?: boolean,
    showHotelRecommended?: boolean,
    showVisaDestinations?: boolean,
    page?: number
  ) => Promise<Model.DataPayload<Model.Poi[]>>;
  // for actions
  focus: (poi: Model.Poi) => void;
  cardButtonOnClick: (poi: Model.Poi, from?: string, to?: string) => void;

  // helper
  isCurrentDayStep: (poiId: string) => boolean;
  getDayIndexes: (poiId: string) => number[];
  gygTourIds: number[];
  bbTourIds: number[];
  viatorTourIds: string[];
  toristyTourIds: string[];
  toursLoading: boolean;
  onBookingClick: (poi: Model.Poi) => void;
  t: (value: Model.TranslationKey) => string;
  skipStepTimesModal?: boolean;
}

const SearchPlace: React.FC<ISearchPlace> = ({
  query,
  poiCategoryIds,
  // recommendedPois,
  search,
  focus,
  cardButtonOnClick,
  isCurrentDayStep,
  getDayIndexes,
  gygTourIds,
  bbTourIds,
  viatorTourIds,
  toristyTourIds,
  toursLoading,
  onBookingClick,
  t,
  skipStepTimesModal = false,
}) => {
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryPageIndex, setQueryPageIndex] = useState(1);
  const [queryResults, setQueryResults] = useState<Model.Poi[] | undefined>(
    undefined
  );
  const [queryFinished, setQueryFinished] = useState(false);

  const { hashParam } = useParams<{ hashParam: string }>();
  const { favorites, favoriteAdd, favoriteDelete, loadingFavoritePoi } =
    useFavorite();

  const shared = useMemo(() => {
    if (!hashParam) return false;
    const params = hashParam.split("!");
    return params.length > 1 && hashParam.split("!")[1] === "s";
  }, [hashParam]);

  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryPageIndex, setCategoryPageIndex] = useState(1);
  const [categoryResults, setCategoryResults] = useState<
    Model.Poi[] | undefined
  >();
  const [categoryFinished, setCategoryFinished] = useState(false);

  const [showOffersOnly, setShowOffersOnly] = useState(false);
  const [showBookingsOnly, setShowBookingsOnly] = useState(false);
  const [showBookingsRestaurant, setShowBookingsRestaurant] = useState(false);
  const [showHotelRecommended, setshowHotelRecommended] = useState(false);
  const [showVisaDestinations, setShowVisaDestinations] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);

  // Filter helper function
  const filterPoiByBookings = (poi: Model.Poi): boolean => !!poi.hasBookings;

  // Compute filtered POIs
  const currentPoiList = queryResults || categoryResults;
  const filteredPoiList = useMemo(() => {
    if (!currentPoiList) return [];
    if (!showBookingsOnly) return currentPoiList;
    return currentPoiList.filter(filterPoiByBookings);
  }, [currentPoiList, showBookingsOnly]);

  // Favorilerde olup olmadığını kontrol eden fonksiyon
  const isFavorite = (poiId: string) =>
    favorites
      ? favorites.findIndex(favorite => favorite.poiId === poiId) > -1
      : false;

  // Favorilere ekle/çıkar fonksiyonu
  const favoriteToggle = (poi: Model.Poi, add: boolean) => {
    if (add) {
      favoriteAdd(poi.id);
    } else {
      const favoriteId: number =
        favorites?.find(favorite => favorite.poiId === poi.id)?.id || 0;
      if (favoriteId) favoriteDelete(favoriteId, poi.id);
      else
        console.error(
          "favoriteToggle favorite delete error: Can not found poi id in favorite list",
          add,
          poi.id,
          favorites
        );
    }
  };

  useEffect(() => {
    const fetchCategoryResults = async () => {
      setCategoryLoading(true);
      setCategoryPageIndex(1);
      setCategoryFinished(false);
      try {
        // console.log("showBookingsOnly 1", showBookingsOnly);
        const newPois = await search(
          [],
          poiCategoryIds,
          showOffersOnly,
          showBookingsOnly,
            showBookingsRestaurant,
            showHotelRecommended,
            showVisaDestinations
        );
        // console.log("newPois1", newPois);
        setCategoryResults(newPois.data);
        if (newPois.pagination?.currentPage === newPois.pagination?.totalPages)
          setCategoryFinished(true);
      } finally {
        setCategoryLoading(false);
      }
    };

    if (poiCategoryIds.length === 1) {
      fetchCategoryResults();
    }
  }, [poiCategoryIds, search, showOffersOnly, showBookingsOnly, showBookingsRestaurant, showHotelRecommended, showVisaDestinations]);

  useEffect(() => {
    const fetchPois = async () => {
      setQueryLoading(true);
      setQueryResults(undefined);
      setQueryPageIndex(1);
      setQueryFinished(false);

      try {
        const searchPoisResult = await search(
          query,
          poiCategoryIds,
          showOffersOnly,
          showBookingsOnly,
          showBookingsRestaurant,
            showHotelRecommended,
            showVisaDestinations
        );

        setQueryResults(searchPoisResult.data);

        if (
          searchPoisResult.pagination?.currentPage ===
          searchPoisResult.pagination?.totalPages
        ) {
          setQueryFinished(true);
        }
      } catch (error) {
        console.error("Error searching POIs:", error);
        setQueryResults([]);
      } finally {
        setQueryLoading(false);
      }
    };

    // Tek kategori + query yoksa Effect 1 (category fetch) hallediyor, burada atla
    if (poiCategoryIds.length === 1 && (!query || query.length === 0)) {
      setQueryResults(undefined);
      return;
    }

    // Kategori ID'leri varsa aramayı yap
    if (poiCategoryIds.length > 0) {
      fetchPois();
    } else {
      // Kategori ID'leri yoksa sonuçları temizle
      setQueryLoading(false);
      setQueryResults(undefined);
    }
  }, [poiCategoryIds, query, search, showOffersOnly, showBookingsOnly, showBookingsRestaurant, showHotelRecommended, showVisaDestinations]);

  const queryLoadMore = async () => {
    if (queryResults) {
      setLoadMoreLoading(true);
      const newPageIndex = queryPageIndex + 1;
      setQueryPageIndex(newPageIndex);
      try {
        const newQueryResults = await search(
          query,
          poiCategoryIds,
          showOffersOnly,
          showBookingsOnly,
          showBookingsRestaurant,
            showHotelRecommended,
            showVisaDestinations,
          newPageIndex
        );
        setQueryResults(
          helper.removeDuplicateValuesById<Model.Poi>(
            queryResults.concat(newQueryResults.data)
          )
        );
        if (
          newQueryResults.pagination?.currentPage ===
          newQueryResults.pagination?.totalPages
        )
          setQueryFinished(true);
      } finally {
        setLoadMoreLoading(false);
      }
    }
  };

  const categoryLoadMore = async () => {
    setLoadMoreLoading(true);
    const newPageIndex = categoryPageIndex + 1;
    setCategoryPageIndex(newPageIndex);
    try {
      const newCategoryResults = await search(
        [],
        poiCategoryIds,
        showOffersOnly,
        showBookingsOnly,
        showBookingsRestaurant,
          showHotelRecommended,
          showVisaDestinations,
        newPageIndex
      );
      setCategoryResults(prevCategoryResult => {
        if (prevCategoryResult) {
          const filteredPrevCategoryResulsts = prevCategoryResult.filter(poi =>
            poi.category.some(p => poiCategoryIds.includes(p.id))
          );
          return helper.removeDuplicateValuesById<Model.Poi>(
            filteredPrevCategoryResulsts.concat(newCategoryResults.data)
          );
        }
        return newCategoryResults.data;
      });
      if (
        newCategoryResults.pagination?.currentPage ===
        newCategoryResults.pagination?.totalPages
      )
        setCategoryFinished(true);
    } finally {
      setLoadMoreLoading(false);
    }
  };

  // Auto-load more when filtered results are empty but there's more data
  useEffect(() => {
    // Only run when showBookingsOnly filter is active
    if (!showBookingsOnly) return;
    // Don't run while loading
    if (queryLoading || categoryLoading || loadMoreLoading) return;
    // Check if we have data but filtered list is empty
    if (!currentPoiList || currentPoiList.length === 0) return;
    if (filteredPoiList.length > 0) return;

    // Determine which pagination to use and if there's more to load
    const isQueryMode = queryResults !== undefined;
    const isFinished = isQueryMode ? queryFinished : categoryFinished;

    if (!isFinished) {
      // Auto-trigger load more
      if (isQueryMode) {
        queryLoadMore();
      } else {
        categoryLoadMore();
      }
    }
  }, [
    showBookingsOnly,
    filteredPoiList,
    currentPoiList,
    queryLoading,
    categoryLoading,
    loadMoreLoading,
    queryResults,
    queryFinished,
    categoryFinished,
  ]);

  const loadMore = () => {
    if (queryLoading || categoryLoading) return null;

    if (queryResults) {
      if (queryResults.length > 19 && !queryFinished)
        return (
          <div className="center m5">
            <Button
              color="primary"
              text={t("trips.myTrips.exploreMore.loadMore")}
              onClick={queryLoadMore}
            />
          </div>
        );
      return null;
    }

    if (!categoryFinished)
      return (
        <div className="center m5">
          <Button
            color="primary"
            text={t("trips.myTrips.exploreMore.loadMore")}
            onClick={categoryLoadMore}
          />
        </div>
      );

    return null;
  };

  const renderList = (poiList?: Model.Poi[]) => {
    if (!poiList) return null;

    const filteredPois = showBookingsOnly
      ? poiList.filter(poi => poi.hasBookings)
      : poiList;

    return filteredPois.map(poiResult => {
      const dayIndexes = getDayIndexes(poiResult.id);
      const isRecommendation = !dayIndexes.includes(-2);
      let dayNumbers: number[] | undefined;
      if (isRecommendation && !dayIndexes.includes(-1)) {
        dayNumbers = dayIndexes.map(dayIndex => dayIndex + 1);
      }

      return (
        <PoiCard
          key={`search-result-poi-${poiResult.id}`}
          poi={poiResult}
          inStep={isCurrentDayStep(poiResult.id)}
          isFavorite={isFavorite(poiResult.id)}
          isLoadingFavorite={loadingFavoritePoi(poiResult.id)}
          dayNumbers={dayNumbers}
          TOUR_PROVIDER_IDS={window.tconfig.TOUR_TICKET_PROVIDER_IDS}
          TICKET_PROVIDER_IDS={window.tconfig.TOUR_TICKET_PROVIDER_IDS}
          RESTAURANT_RESERVATION_PROVIDER_IDS={
            window.tconfig.RESTAURANT_RESERVATION_PROVIDER_IDS
          }
          hideTourTicketIcons={!window.tconfig.SHOW_TOURS_AND_TICKETS}
          gygTourIds={gygTourIds}
          bbTourIds={bbTourIds}
          viatorTourIds={viatorTourIds}
          toristyTourIds={toristyTourIds}
          tourTicketProductsLoading={toursLoading}
          poiCardClicked={focus}
          onAddPoiClick={cardButtonOnClick}
          onFavoriteToggle={favoriteToggle}
          onBookingClick={onBookingClick}
          t={t}
          skipStepTimesModal={skipStepTimesModal}
        />
      );
    });
  };

  const renderResult = () => {
    if (queryLoading || categoryLoading)
      return (
        <div style={{ height: "4rem" }}>
          <PreLoading size="small" />
        </div>
      );

    if (queryResults) {
      if (queryResults.length === 0)
        return (
          <div className="text-center text-text-primary-color text-sm min-h-16">
            {t("trips.myTrips.exploreMore.noResultsFound")}
          </div>
        );
      return renderList(queryResults);
    }

    return renderList(
      helper.removeDuplicateValuesById<Model.Poi>(categoryResults || [])
    );
  };

  return (
    <div>
      {poiCategoryIds.length > 0 ? (
        <>
          {(
            <div className="py-4 mb-4">
              <CustomSlider scrollSize={150}>
                <button
                  onClick={() => setShowOffersOnly(!showOffersOnly)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border border-primary-color-200 min-h-[40px] transition-all whitespace-nowrap ${
                    showOffersOnly
                      ? "bg-primary-color text-white"
                      : "bg-white text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <SvgIcons.Offer
                    fill={showOffersOnly ? "#fff" : "var(--primary-color)"}
                  />
                  <span className="font-medium text-sm">
                    {t("trips.myTrips.exploreMore.onlyWithOffers")}
                  </span>
                </button>

                <button
                  onClick={() => setShowBookingsOnly(!showBookingsOnly)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border border-primary-color-200 min-h-[40px] transition-all whitespace-nowrap ${
                    showBookingsOnly
                      ? "bg-primary-color text-white"
                      : "bg-white text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <SvgIcons.Booking
                    fill={showBookingsOnly ? "#fff" : "var(--primary-color)"}
                  />
                  <span className="font-medium text-sm">
                    {t("trips.myTrips.exploreMore.onlyWithBookings")}
                  </span>
                </button>
                {window.tconfig.KEY_FLAGS?.includes("volo") && (
                  <button
                    onClick={() => setshowHotelRecommended(!showHotelRecommended)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border border-primary-color-200 min-h-[40px] transition-all whitespace-nowrap ${
                      showHotelRecommended
                        ? "bg-primary-color text-white"
                        : "bg-white text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <SvgIcons.Booking
                      fill={showHotelRecommended ? "#fff" : "var(--primary-color)"}
                    />
                    <span className="font-medium text-sm">
                      {t("trips.myTrips.exploreMore.hotelRecommended")}
                    </span>
                  </button>
                )}

                {window.tconfig.BRAND_NAME === "Visa" && window.tconfig.KEY_FLAGS?.includes("visa") && (
                  <button
                    onClick={() => setShowVisaDestinations(!showVisaDestinations)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border border-primary-color-200 min-h-[40px] transition-all whitespace-nowrap flex-shrink-0 ${
                      showVisaDestinations
                        ? "bg-primary-color text-white"
                        : "bg-white text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <img
                      src={showVisaDestinations ? window.tconfig.IMAGE_PATHS.LOGO_PATH_DARK : window.tconfig.IMAGE_PATHS.LOGO_PATH_LIGHT}
                      alt="Visa"
                      className="h-4 w-auto"
                    />
                    <span className="font-medium text-sm">
                      {t("trips.myTrips.localExperiences.tourDetails.experience.destinations")}
                    </span>
                  </button>
                )}
              </CustomSlider>
            </div>
          )}

          {renderResult()}

          {loadMoreLoading ? (
            <div style={{ height: "4rem" }}>
              <PreLoading size="small" />
            </div>
          ) : (
            loadMore()
          )}
        </>
      ) : (
        <div className="text-center text-text-primary-color text-sm min-h-16 mt-8">
          {t("trips.myTrips.exploreMore.pleaseSelectCategory")}
        </div>
      )}
    </div>
  );
};

export default SearchPlace;
